import React__default from 'react';
import { SearchQueryPillWidget } from '@brandclub/bc-widgets';

// Helper to convert font weight string to CSS value
const getFontWeight = (weight) => {
    const weightMap = {
        normal: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
    };
    return weightMap[weight] || 500;
};
/**
 * A pill-shaped badge displaying a search query with overlapping product images.
 * Wrapped with SearchQueryPillWidget for theme customization support.
 */
const SearchQueryPill = ({ query, products = [], totalResults = 0, isLoading = false, isAnimating = false, className, }) => {
    const displayProducts = products.slice(0, 4);
    const remainingCount = Math.max(0, totalResults - displayProducts.length);
    const hasProducts = displayProducts.length > 0;
    // Reserve space for images even when loading to prevent layout shift
    const showImagePlaceholder = isLoading || hasProducts;
    return (React__default.createElement(SearchQueryPillWidget, { widgetId: "search-query-pill", displayName: "Search Query Pill" }, (state) => (React__default.createElement("div", { className: className, style: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            backgroundColor: state.backgroundColor,
            border: `${state.borderWidth}px solid ${state.borderColor}`,
            borderRadius: state.borderRadius,
            padding: `${state.paddingY}px ${state.paddingX}px`,
            transition: 'all 0.3s',
            maxWidth: '100%',
            animation: isLoading ? 'bcSubtlePulse 2.5s ease-in-out infinite' : undefined,
        } },
        React__default.createElement("span", { style: {
                fontSize: state.fontSize,
                fontWeight: getFontWeight(state.fontWeight),
                fontFamily: state.fontFamily,
                color: isLoading ? state.textColorLoading : state.textColor,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                transition: 'color 0.3s',
            }, title: query }, query),
        showImagePlaceholder && (React__default.createElement("div", { style: { display: 'flex', alignItems: 'center', flexShrink: 0 } }, isLoading ? (
        // Placeholder circles during loading
        [0, 1, 2, 3].map((idx) => (React__default.createElement("div", { key: idx, style: {
                width: state.imageSize,
                height: state.imageSize,
                borderRadius: state.imageBorderRadius,
                backgroundColor: state.placeholderBgColor,
                border: `1px solid ${state.imageBorderColor}`,
                flexShrink: 0,
                position: 'relative',
                zIndex: 4 - idx,
                marginLeft: idx > 0 ? -state.imageOverlap : 0,
            } })))) : (
        // Actual product images
        React__default.createElement(React__default.Fragment, null,
            displayProducts.map((product, idx) => (React__default.createElement("a", { key: `${product.stacklineSku}-${idx}`, href: `/placeholder/p/${product.stacklineSku}`, target: "_blank", rel: "noopener noreferrer", title: product.title, style: {
                    width: state.imageSize,
                    height: state.imageSize,
                    borderRadius: state.imageBorderRadius,
                    overflow: 'hidden',
                    backgroundColor: '#ffffff',
                    border: `1px solid ${state.imageBorderColor}`,
                    flexShrink: 0,
                    display: 'block',
                    position: 'relative',
                    zIndex: displayProducts.length - idx,
                    marginLeft: idx > 0 ? -state.imageOverlap : 0,
                    opacity: isAnimating ? 0 : 1,
                    transform: isAnimating ? 'scale(0.5)' : 'scale(1)',
                    animation: isAnimating ? `pillProductFadeIn 0.4s ease-out ${idx * 100}ms forwards` : 'none',
                    transition: 'transform 0.3s',
                } },
                React__default.createElement("img", { src: product.imageUrl, alt: product.title, style: { width: '100%', height: '100%', objectFit: 'cover' } })))),
            remainingCount > 0 && (React__default.createElement("span", { style: {
                    fontSize: state.counterFontSize,
                    fontWeight: getFontWeight(state.fontWeight),
                    fontFamily: state.fontFamily,
                    color: state.counterTextColor,
                    marginLeft: '8px',
                    flexShrink: 0,
                    opacity: isAnimating ? 0 : 1,
                    animation: isAnimating
                        ? `pillProductFadeIn 0.4s ease-out ${displayProducts.length * 100}ms forwards`
                        : 'none',
                }, title: `${remainingCount} more products` },
                "+",
                remainingCount)))))),
        React__default.createElement("style", null, `
            @keyframes pillProductFadeIn {
              from {
                opacity: 0;
                transform: scale(0.5);
              }
              to {
                opacity: 1;
                transform: scale(1);
              }
            }
            @keyframes bcSubtlePulse {
              0%, 100% {
                opacity: 1;
              }
              50% {
                opacity: 0.65;
              }
            }
          `)))));
};

export { SearchQueryPill, SearchQueryPill as default };
//# sourceMappingURL=SearchQueryPill.js.map
